import React from 'react'
import { Logo } from '../../components/simple/logo'
import { DropdownDivider, dropdownItemStyle, DropdownLabel } from '../../components/simple/dropdown'
import {
  IconAddressCard,
  IconAngleRight,
  IconBell,
  IconCalendar,
  IconChartArea,
  IconChartBar,
  IconChartPie,
  IconCheckMark,
  IconComments,
  IconCommentSlash,
  IconFileDownload,
  IconFileUpload,
  IconGear,
  IconGraduationCap,
  IconHamburger,
  IconHeadset,
  IconHome,
  IconInfoCircle,
  IconLexikeet,
  IconLock,
  IconLogOut,
  IconPhone,
  IconStream,
  IconTable,
  IconTeacher,
  IconTimes,
  IconUser,
} from '../../icons/library'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { NavAction, NavItem, NavLink, NavMenu, NavMenuGroup } from '../../components/simple/navMenu'
import { AvatarButton } from '../../components/avatar'
import { User } from '../../providers/context'
import { UserCard } from '../../components/userCard'
import { linkTo, mapWithDivider } from '../../utils'
import { useTranslation } from '../../providers/translation'
import { IconHelp } from '../../icons/help'
import { useLocation, useNavigate } from 'react-router-dom'
import { AccountSwitchButton } from '../../pages/accountSwitcher'
import { useNotifications } from '../../providers/pushNotificationProvider'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { getComposeLink, getDirectLink, getRelativeLink, URLAction } from '../../providers/URLParams'

type HeaderOption = {
  to: string
  icon?: React.ReactNode
  badge?: React.ReactNode
  text: string
  description?: string
  enabled?: boolean
  showOnMobile?: boolean
}

type HeaderGroup = {
  icon: React.ReactNode
  style: ButtonStyle
  enabled?: boolean
  showOnMobile?: boolean
  options: HeaderOption[]
  buttonID?: string
}
interface StandardHeaderItemsProps {
  user: User
  onToggleLeft?: () => void
  extra?: React.ReactNode
}

export const StandardHeaderItems: React.FC<StandardHeaderItemsProps> = React.memo(({ user, onToggleLeft, extra }) => {
  return (
    <>
      <Logo size="sm" link="/" className="hidden sm:flex" showTM showName />
      <Button
        buttonStyle={ButtonStyle.ghostLogoTransparent}
        shape={ButtonShape.icon}
        className="sm:hidden"
        onClick={onToggleLeft}
      >
        <IconHamburger size="lg" />
      </Button>
      {extra}
      <NavMenuSection user={user} />
    </>
  )
})

const NavMenuSection: React.FC<{ user: User }> = React.memo(({ user }) => {
  const { tr, language } = useTranslation()
  const location = useLocation()
  const { notificationsEnabled } = useNotifications()
  const home = location.pathname === '/'

  const standardHeaderGroups: HeaderGroup[] = React.useMemo(() => {
    const c: HeaderGroup[] = [
      {
        icon: <b>Admin</b>,
        style: ButtonStyle.ghostLogoTransparent,
        enabled: user.district.isAdmin,
        options: [
          {
            to: '/add-demo-school',
            text: 'New Demo',
          },
          {
            to: '/composeSystemMessage',
            text: 'System message',
          },
          {
            to: '/adminStatus',
            text: 'Status Update',
          },
          {
            to: '/addDistrict',
            text: 'New District',
            enabled: !user.faria,
          },
          {
            to: '/getAllIntegrations',
            text: 'Get All Integration',
          },
          {
            to: '/getAllAttendanceMessages',
            text: 'All Attendance Messages',
          },
          {
            to: '/getAllSFTPFiles',
            text: 'All SFTP Files',
          },
          {
            to: '/realTime',
            text: 'Real Time',
          },
          {
            to: '/getAdminFunctions',
            text: 'Admin Functions',
          },
        ],
      },
      {
        icon: <IconLexikeet />,
        style: ButtonStyle.ghostGreenTransparent,
        enabled: user.type === 'teacher' && user.teacherLexikeet,
        options: [
          {
            to: '/lkUploadFile',
            icon: <IconFileUpload />,
            text: 'Translate File',
          },
          {
            to: '/lkUploadFile',
            icon: <IconFileDownload />,
            text: 'Retrieve Translated File',
          },
          {
            to: getComposeLink({ type: 'phone' }),
            icon: <IconHeadset />,
            text: 'Interpreted Call With Student',
          },
          {
            to: getDirectLink(URLAction.OpenGeneralLexikeetCall),
            icon: <IconHeadset />,
            text: 'General Interpreted Call',
          },
        ],
      },
      {
        icon: <IconGear />,
        style: ButtonStyle.ghostLogoTransparent,
        options: [
          {
            to: getRelativeLink(URLAction.OpenEditUser),
            icon: <IconUser />,
            text: tr('User Preferences'),
            showOnMobile: true,
          },
          {
            to: '/pushNotificationSetup',
            text: tr('Push Notifications'),
            icon: <IconBell />,
            badge: notificationsEnabled ? (
              <Badge badgeStyle={BadgeStyle.green} tooltip="Notifications enabled">
                <IconCheckMark />
              </Badge>
            ) : (
              <Badge badgeStyle={BadgeStyle.red} tooltip="Notifications disabled">
                <IconTimes />
              </Badge>
            ),
            enabled: user.type === 'teacher',
            showOnMobile: true,
          },
          {
            to: getRelativeLink(URLAction.OpenAbbreviationConfig),
            text: user.type === 'teacher' && user.districtAccess ? 'District Abbreviation' : 'School Abbreviation',
            icon: <IconGear />,
            enabled: user.type === 'teacher' && user.admin > 0,
          },
          {
            to: getRelativeLink(URLAction.OpenCallerIDConfig),
            text: user.type === 'teacher' && user.districtAccess ? 'District Caller ID' : 'School Caller ID',
            icon: <IconPhone />,
            enabled: user.type === 'teacher' && user.admin > 0 && user.district.phoneEnabled,
          },
          {
            to: getRelativeLink(URLAction.OpenDistrictSettings),
            text: user.faria ? 'School Settings' : 'District Settings',
            icon: <IconLock />,
            enabled: user.type === 'teacher' && user.admin == 2 && (user.districtAccess || user.faria),
          },
          {
            to: getRelativeLink(URLAction.OpenAttendanceSettings),
            text: 'Attendance Settings',
            icon: <IconCalendar />,
            enabled: user.type == 'teacher' && user.showAttendance,
          },
        ],
      },
      {
        icon: <IconAddressCard />,
        style: ButtonStyle.ghostLogoTransparent,
        enabled: user.type === 'teacher' && !user.externalStaff,
        options: [
          {
            to: '/contacts/families',
            icon: <IconGraduationCap />,
            text: 'Student Contacts',
            description: 'edit user info and view stats',
            showOnMobile: true,
          },
          {
            to: '/contacts/teachers',
            icon: <IconTeacher />,
            text: 'Staff Contacts',
            description: 'edit staff info and view stats',
            enabled: user.type === 'teacher' && user.admin > 0,
            showOnMobile: true,
          },
          {
            to: '/contacts/report',
            icon: <IconCommentSlash />,
            text: 'Contacts Report',
            description: 'see edited and failed contact info',
            enabled: user.type === 'teacher' && user.admin > 0,
          },
        ],
      },
      {
        icon: <IconChartArea />,
        style: ButtonStyle.ghostLogoTransparent,
        enabled: user.type === 'teacher' && !user.externalStaff,
        options: [
          {
            to: '/dashboard',
            icon: <IconChartPie />,
            text: 'Dashboard',
            description: 'usage overview',
            enabled: user.type === 'teacher' && user.admin > 0,
          },
          {
            to: '/stats/district',
            icon: <IconTable />,
            text: user.type === 'teacher' && user.districtAccess ? 'District Stats' : 'School Stats',
            description:
              user.type === 'teacher' && user.districtAccess ? 'view stats by school' : 'view school-wide stats',
            enabled: user.type === 'teacher' && user.admin > 0,
          },
          {
            to: '/admin/stats',
            icon: <IconTable />,
            text: 'System Stats',
            description: 'view system stats',
            enabled: user.district.isAdmin,
          },
          {
            to: `/integration/${user.district.id}`,
            icon: <IconStream />,
            text: 'Integration Status',
            description: 'data import info',
            enabled: user.type === 'teacher' && user.admin === 2 && user.districtAccess,
          },
          {
            to: '/stats/yours',
            icon: <IconChartBar />,
            text: 'Your Communication',
            description: 'see your stats',
          },
        ],
      },
      {
        icon: <IconHelp />,
        style: ButtonStyle.ghostLogoTransparent,
        buttonID: 'tutorial_menuTooltip',
        options: [
          {
            icon: <IconTeacher />,
            text: tr('View Tutorial'),
            to: getRelativeLink(URLAction.OpenTutorial),
            enabled: (user.type !== 'teacher' || !user.externalStaff) && home,
          },
          {
            icon: <IconHelp />,
            text: tr('Help Resources'),
            to: 'https://reachmyteach.zendesk.com',
            enabled: !user.faria,
            showOnMobile: true,
          },
          {
            icon: <IconInfoCircle />,
            text: tr('Student Information'),
            to: 'https://reachmyteach.zendesk.com/hc/en-us/articles/19184825148057',
            enabled: user.type === 'guardian',
            showOnMobile: true,
          },
          {
            icon: <IconComments />,
            text: tr('Give Feedback'),
            to: getRelativeLink(URLAction.OpenFeedback),
            enabled: !user.faria && user.type === 'teacher',
          },
        ],
      },
    ]
    return c
  }, [user, notificationsEnabled, language])

  const navigate = useNavigate()
  return (
    <NavMenuGroup className="ml-auto">
      {!home && (
        <Button
          buttonStyle={ButtonStyle.ghostLogoTransparent}
          shape={ButtonShape.icon}
          onClick={() => {
            navigate('/')
          }}
        >
          <IconHome />
        </Button>
      )}
      {standardHeaderGroups
        .filter((a) => a.enabled !== false)
        .map((action, index) => (
          <StandardActionMenu key={index} action={action} />
        ))}
      <NavMenu button={<AvatarButton src={user.avatar} />}>
        <NavItem>
          <UserCard user={user} />
        </NavItem>
        <DropdownDivider full />
        {standardHeaderGroups
          .filter((a) => a.enabled !== false)
          .flatMap((action, outer_index) =>
            action.options
              ?.filter((o) => o.enabled !== false && o.showOnMobile)
              .map((option, index) => (
                <OptionLink className="sm:hidden" key={index + ' ' + outer_index} option={option} />
              ))
          )}
        <DropdownDivider full className="sm:hidden" />
        {user.otherAccounts.length > 0 && (
          <>
            <DropdownLabel>{tr('Switch Accounts')}</DropdownLabel>
            {mapWithDivider(
              user.otherAccounts,
              (curr, prev) => curr.role != prev.role,
              (index) => (
                <DropdownDivider key={index} className="my-1" />
              ),
              (otherUser, index) => (
                <NavAction key={index}>
                  <AccountSwitchButton user={otherUser} className={`${dropdownItemStyle} flex gap-2`}>
                    <IconAngleRight
                      className={`my-auto ml-auto text-link ${otherUser.canSwitchToAccount ? '' : 'opacity-0'} `}
                      size="xs"
                      thicken={20}
                    />
                  </AccountSwitchButton>
                </NavAction>
              )
            )}
            <DropdownDivider full className="mt-1" />
          </>
        )}
        <NavLink to="/logout" className="mt-1 text-sm" style={ButtonStyle.ghostRedTransparent}>
          <IconLogOut className="mr-1" />
          {tr('Logout')}
        </NavLink>
        <NavItem className="justify-between gap-2 !px-1 pt-0 text-sm opacity-75">
          <a
            href="https://reachmyteach.zendesk.com/hc/en-us/articles/16253003439385-ReachMyTeach-Terms-of-Service"
            className="text-nowrap no-underline hover:underline"
          >
            {tr('terms of service')}
          </a>
          <a
            href="https://reachmyteach.zendesk.com/hc/en-us/articles/16253106902169-ReachMyTeach-Privacy-Policy"
            className="text-nowrap no-underline hover:underline"
          >
            {tr('privacy policy')}
          </a>
        </NavItem>
      </NavMenu>
    </NavMenuGroup>
  )
})

const StandardActionMenu: React.FC<{ action: HeaderGroup }> = ({ action }) => {
  return (
    <NavMenu
      className="hidden sm:flex"
      button={
        <Button buttonStyle={action.style} shape={ButtonShape.icon} id={action.buttonID}>
          {action.icon}
        </Button>
      }
    >
      {action.options
        ?.filter((o) => o.enabled !== false)
        .map((option, index) => <OptionLink key={index} option={option} />)}
    </NavMenu>
  )
}

const OptionLink: React.FC<{ option: HeaderOption; className?: string }> = ({ option, className }) => {
  const { tr } = useTranslation()
  return (
    <NavLink to={option.to} className={`w-full gap-5 py-2 ${className}`}>
      <span className="ml-2 scale-125 text-link">{option.icon}</span>
      <span className="flex flex-col text-nowrap leading-5">
        <span className="font-semibold">{tr(option.text)}</span>
        {option.description && <span className="text-[13px]">{tr(option.description)}</span>}
      </span>
      {option.badge}
      <IconAngleRight className="ml-auto text-link" size="xs" thicken={20} />
    </NavLink>
  )
}
